import { BuildingMenuType } from '@/enums';
import { getPoiListImageUrlFromContent } from '@/utils/poi/poiImage';
import { SHOP_SEARCH_MODE } from '@/enums/shop';
import type { BuildingInStoreInfo, BuildingMenu, FloorInfo, PoiContent } from '@/services/dto';
import type { ShopFilterConfig } from '@/stores/types/shopStore';
import type { PoiContentSlide } from '@/types/shop';
import poiUtils from '@/utils/poi';

export const getSearchMenu = (buildingMenus: Record<string, BuildingMenu[]> | null | undefined, language: string): BuildingMenu | undefined => {
    const menus = buildingMenus?.[language] ?? [];
    return menus.find((menu) => menu.menuType === BuildingMenuType.SEARCH);
};

export const resolveShopPoiContents = (searchMenu: BuildingMenu | undefined, selectedBuildingId: string | null, searchMode: SHOP_SEARCH_MODE): PoiContent[] => {
    if (!searchMenu) return [];

    const isFloorModeWithBuilding = searchMode === SHOP_SEARCH_MODE.SEARCH_BY_FLOOR && selectedBuildingId && selectedBuildingId !== 'all';

    if (isFloorModeWithBuilding && searchMenu.poiContentBuildings?.length) {
        const match = searchMenu.poiContentBuildings.find((b) => b.id === selectedBuildingId);
        return match?.poiContents ?? searchMenu.poiContents ?? [];
    }

    return searchMenu.poiContents ?? [];
};

export const convertPoiContentToSlide = (lang: string, buildings: BuildingInStoreInfo[], pois?: PoiContent[], searchText?: string): PoiContentSlide[] => {
    const buildingById = new Map(buildings.map((b) => [b.studioBuildingId, b]));

    const allPois: { poi: PoiContent; building: BuildingInStoreInfo }[] = pois
        ? pois.flatMap((poi) => {
              const building = buildingById.get(poi.studioBuildingId);
              return building ? [{ poi, building }] : [];
          })
        : [];

    return allPois?.map(({ poi, building }) => {
        const contents = poi.contents;

        const formatData = {
            name: poi.name?.[lang] ? poi.name?.[lang] : poi.name?.['ko'],
            shopUrl: getPoiListImageUrlFromContent(poi, lang),
        };

        const name = formatData.name || '';
        const shopUrl = formatData.shopUrl || '';
        const floor = poiUtils.resolveFloorSlideLabel(poiUtils.getFloorName(lang, poi.studioFloorId, building.floors ?? []));
        const buildingName = poiUtils.getBuildingLocalizedName(building, lang);
        const poiCategoryCodes = poiUtils.getPoiCategoryCodes(poi);

        const finalCategory = building.filterCategories.shop.find((f) => poiCategoryCodes.includes(f.categoryCode));

        const poiType = 'normal';

        return {
            id: poi.id,
            name,
            buildingName,
            floor,
            url: shopUrl,
            data: poi,
            poiType,
            category: finalCategory ?? undefined,
        };
    });
};

export const filterByFloor = (shop: PoiContentSlide, config: ShopFilterConfig): boolean => {
    const { selectedBuildingId, selectedFloor, buildings } = config;

    // Fallback: Case: specific building selected (old logic)
    if (selectedBuildingId && selectedBuildingId !== 'all') {
        const building = buildings?.find((b) => b.id === selectedBuildingId);
        if (!building) return false;

        if (selectedFloor?.id === 'all') {
            return shop.data.studioBuildingId === building.studioBuildingId;
        }

        return shop.data.studioFloorId === (selectedFloor?.data as FloorInfo)?.floor_code && shop.data.studioBuildingId === building.studioBuildingId;
    }

    // Case: all buildings mode
    if (selectedBuildingId === 'all') {
        if (selectedFloor?.id === 'all') return true;

        if (Array.isArray(selectedFloor?.data)) {
            return (selectedFloor.data as string[]).includes(shop.data.studioFloorId);
        }

        return false;
    }

    return false;
};

export const filterByCategory = (shop: PoiContentSlide, config: ShopFilterConfig): boolean => {
    const { selectedCategory, selectedBuildingId, buildings, categoryCodesForFilter } = config;

    if (!selectedCategory) return false;

    if (selectedBuildingId && selectedBuildingId !== 'all') {
        const building = buildings?.find((b) => b.id === selectedBuildingId);
        if (!building) return false;
        if (shop.data.studioBuildingId !== building.studioBuildingId) {
            return false;
        }
    }

    if (selectedCategory.clientCode === 'all') {
        return true;
    }

    if (!categoryCodesForFilter?.length) return false;

    const poiCategoryCodes = poiUtils.getPoiCategoryCodes(shop.data);
    return categoryCodesForFilter.some((code) => poiCategoryCodes.includes(code));
};

export const filterByText = (shop: PoiContentSlide, config: ShopFilterConfig, keyboardLanguage: 'ko' | 'en'): boolean => {
    const { searchText, language, selectedBuildingId, buildings } = config;

    if (selectedBuildingId && selectedBuildingId !== 'all') {
        // Fallback: use selectedBuildingId if storeCode/buildingCode not available
        const building = buildings?.find((b) => b.id === selectedBuildingId);
        if (!building) return false;
        if (shop.data.studioBuildingId !== building.studioBuildingId) {
            return false;
        }
    }

    if (!searchText?.trim()) return true;

    const nameKeyboard = shop.data.name[keyboardLanguage] || '';
    const nameDefault = shop.data.name[language] || '';
    const normalizedSearchText = searchText.trim();

    return poiUtils.searchByText(nameKeyboard, normalizedSearchText) || poiUtils.searchByText(nameDefault, normalizedSearchText);
};

export const createShopFilter = (searchMode: SHOP_SEARCH_MODE, config: ShopFilterConfig, keyboardLanguage: 'ko' | 'en') => {
    const filterMap = {
        [SHOP_SEARCH_MODE.SEARCH_BY_FLOOR]: filterByFloor,
        [SHOP_SEARCH_MODE.SEARCH_BY_CATEGORY]: filterByCategory,
        [SHOP_SEARCH_MODE.SEARCH_BY_TEXT]: filterByText,
    };

    const filterFunction = filterMap[searchMode];

    return (shop: PoiContentSlide) => {
        return filterFunction ? filterFunction(shop, config, keyboardLanguage) : true;
    };
};

const shopUtils = {
    convertPoiContentToSlide,
    createShopFilter,
    filterByFloor,
    filterByCategory,
    filterByText,
    getSearchMenu,
    resolveShopPoiContents,
};

export default shopUtils;
